import { useEffect, useRef, useState, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  TableContainer,
  TableContent,
  TableHeader,
  TABLE_LAYOUT_OPTIONS,
  usePreventTableScrollingRef,
} from "cdk-radial";
import classNames from "classnames";
import {
  EmptyStatesIllustration,
  IconDelete,
  IconButton,
  Dialog,
  BUTTON_VARIANTS,
  withTooltip,
  TOOLTIP_HORIZONTAL_ALIGNMENTS,
  TOOLTIP_VERTICAL_ALIGNMENTS,
  THEMES,
  IndeterminateProgressCircle,
} from "cdk-radial";
import "./styles.css";
import axios from "axios";
import config from "../config/app.conf.json";
import { capitalizeVisibility, getTypeOfApis } from "../Utils/constants";

const EllipsisCellRenderer = ({ value }) => (
  <div className="ellipsis_cell_renderer">{value || ""}</div>
);

const CustomNoRowsOverlay = ({
  selectedFields
}) => (
  <div className="empty_no_rows_overlay">
    <EmptyStatesIllustration size={180} />
    <div className="empty_state_illustration_text">
    {selectedFields ? "No APIs Selected" : "This org has no contracted APIs"}
    </div>
  </div>
);

export function formatDate(dateString) {
  const date = new Date(dateString);
  const options = { month: "short", day: "numeric", year: "numeric" };
  let formattedDate = date.toLocaleDateString("en-US", options);

  // Add the ordinal suffix to the day
  const day = date.getDate();
  const suffixes = ["th", "st", "nd", "rd"];
  const suffix =
    suffixes[
      day % 10 < 4 && day % 10 > 0 && (day < 11 || day > 13) ? day % 10 : 0
    ];
  formattedDate = formattedDate.replace(/,/, "").replace(/(\d+)/, "$1" + suffix);
  return formattedDate;
}

const DateCellRenderer = ({ value }) => (
  <div className="ellipsis_cell_renderer">{formatDate(value)}</div>
);

export default function ContractedApisGrid({
  contractedApis = [],
  auth,
  setIsLoading,
  setShowToast,
  setToastMessage,
  setToastType,
  selectedApis,
  selectedFields,
  setSelectedApis,
}) {
  const [data, setData] = useState([]);
  const [contractedApiCount, setContractedApiCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [elementToBeDeleted, setElementToBeDeleted] = useState([]);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const { accessToken } = auth;
  const gridRef = useRef();
  const tableContentRef = usePreventTableScrollingRef();

  const DeleteContractActionButtonRenderer = ({ data }) => {
    const isDisabled = data?.allApiDetails?.hasIntegration || false;

    const removeApiContractButtonTooltipProps = {
      id: "remove_api_contract_tooltip-id",
      text: "This API is in use and can't be deleted",
      theme: THEMES.LIGHT,
    };
    const HelpRenderTooltipButtonOne = (props) => {
      return (
        <IconButton
          data-testid="delete-api-contract-button"
          icon={<IconDelete />}
          isDisabled={isDisabled}
          text="Button"
          {...props}
        />
      );
    };
    const IconWithTooltip = withTooltip(
      HelpRenderTooltipButtonOne,
      removeApiContractButtonTooltipProps
    );
    if (isDisabled) {
      return (
        <IconWithTooltip
          horizontalAlignment={TOOLTIP_HORIZONTAL_ALIGNMENTS.LEFT}
          marginAroundElement={8}
          verticalAlignment={TOOLTIP_VERTICAL_ALIGNMENTS.TOP}
        />
      );
    } else {
      return (
        <IconButton
          data-testid="delete-api-contract-button"
          icon={<IconDelete />}
          text="Button"
          onClick={() => {
            if (!selectedFields) {
              setDeleteDialogOpen(true);
              setElementToBeDeleted(data?.allApiDetails);
            } else {
              setSelectedApis((state) =>
                state.filter(
                  (api) => api.api_id !== data?.allApiDetails?.api_id
                )
              );
            }
          }}
        />
      );
    }
  };
  const handleDeleteContract = async () => {
    setDeleteInProgress(true);
    const createToastMessage = (type) => {
      return type === "success" ? (
        <div>
          Contract for API <b>{elementToBeDeleted.contractName}</b> has been
          successfully removed.
        </div>
      ) : (
        "Error removing contract"
      );
    };

    try {
      await axios.post(
        `${config.api.api_gateway_url}/admin/contracts/delete`,
        [
          {
            id: elementToBeDeleted?.contractId,
            type: elementToBeDeleted?.contractType,
            orgId: elementToBeDeleted?.organizationId,
          },
        ],
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
          },
        }
      );

      // Successful deletion
      setToastMessage(createToastMessage("success"));
      setToastType("Positive");
    } catch (error) {
      console.error(
        "Error deleting contracts:",
        error.response?.data || error.message
      );
      setToastMessage(createToastMessage("error"));
      setToastType("Negative");
    } finally {
      // Common state updates
      setDeleteDialogOpen(false);
      setDeleteInProgress(false);
      setIsLoading(true);
      setShowToast(true);
    }
  };
  // Memoize column definitions to avoid re-creation on every render
  const columns = useMemo(
    () => [
      {
        cellRendererFramework: EllipsisCellRenderer,
        field: "apiName",
        headerName: "API Name",
        minWidth: 350,
        sortable: true,
      },
      {
        cellRendererFramework: EllipsisCellRenderer,
        field: "typeOfApis",
        headerName: "API Type",
        sortable: true,
        minWidth: 100,
        maxWidth: 220,
      },
      {
        cellRendererFramework: EllipsisCellRenderer,
        field: "publisher",
        headerName: "Publisher",
        minWidth: 250,
      },
      ...(!selectedFields
        ? [
            {
              cellRendererFramework: DateCellRenderer,
              field: "contractedOn",
              headerName: "Created On",
              minWidth: 200,
            },
          ]
        : []),
      {
        cellRendererFramework: EllipsisCellRenderer,
        field: "visibility",
        headerName: "Visibility",
        minWidth: 200,
      },
      {
        field: "action",
        headerName: "Action",
        minWidth: 20,
        cellRendererFramework: DeleteContractActionButtonRenderer,
      },
    ],
    []
  );

  // Memoize header props to avoid unnecessary re-renders
  const headerProps = useMemo(
    () => ({
      dataTestId: `contracted-apis-table-header`,
      headerTitle: selectedFields ? (
        <div>
          Selected APIs
          {selectedApis?.length > 0 ? ` (${selectedApis.length})` : ""}
        </div>
      ) : (
        <div>
          Contracted APIs
          {contractedApiCount > 0 ? ` (${contractedApiCount})` : ""}
        </div>
      ),
      hideBorder: false,
      hideTableHeader: false,
      hideTableTitle: false,
      id: `contracted-apis-table-header`,
      isAlwaysExpanded: false,
      isSearchable: false,
      isPrintable: false,
      isDownloadable: false,
      overflowButtonProps: undefined,
    }),
    [contractedApiCount,selectedApis?.length]
  );

  useEffect(() => {
    let tableData;

    if (selectedFields) {
      tableData =
        selectedApis?.map((api) => ({
          apiName: api?.api_name,
          typeOfApis:getTypeOfApis(api?.api_type),
          publisher: api?.org_name,
          contractedOn: api?.org_name,
          visibility: capitalizeVisibility(api?.visibility),
          allApiDetails: api,
        })) || [];
    } else {
      tableData =
        contractedApis?.map((api) => ({
          apiName: api?.contractName,
          typeOfApis:getTypeOfApis(api?.contractType),
          publisher: api?.publisherName,
          contractedOn: api?.created,
          visibility: capitalizeVisibility(api?.visibility),
          allApiDetails: api,
        })) || [];
    }
    setData(tableData);
    setContractedApiCount(contractedApis?.length || 0);
  }, [selectedApis, contractedApis, selectedFields]);

  const handleGridReady = (gridParams) => {
    gridParams.api.sizeColumnsToFit();
    gridParams.api.refreshCells({ force: true });
    gridParams.api.showNoRowsOverlay();
  };

  return (
    <TableContainer
      id="ag-grid-container"
      className={classNames("contracted-apis_grid", "ag-theme-balham")}
    >
      <TableHeader {...headerProps} />

      <TableContent
        data-testid="table-content"
        className="contracted-apis_grid-content"
        ref={tableContentRef}
      >
        <AgGridReact
          columnDefs={columns}
          domLayout={TABLE_LAYOUT_OPTIONS.AUTO_HEIGHT}
          rowData={data}
          rowHeight={50}
          style={{ width: "100%" }}
          ref={gridRef}
          onGridReady={handleGridReady}
          suppressRowClickSelection={true}
          frameworkComponents={{
            customNoRowsOverlay: (params) => (
              <CustomNoRowsOverlay {...params} selectedFields={selectedFields} />
            ),
          }}
          noRowsOverlayComponent="customNoRowsOverlay"
          suppressColumnVirtualisation
          suppressRowVirtualisation
        />
      </TableContent>
      {
        <Dialog
          buttonsProps={[
            {
              id: "remove_api_contract_dialog-action-1",
              onClick: function noRefCheck() {
                setDeleteDialogOpen(false);
              },
              text: "Cancel",
              variant: BUTTON_VARIANTS.TEXT,
            },
            {
              id: "remove_api_contract_dialog-action-2",
              dataTestId: "shareConfirm",
              onClick: function noRefCheck() {
                handleDeleteContract();
              },
              text: deleteInProgress ? (
                <IndeterminateProgressCircle size="small" />
              ) : (
                "Delete"
              ),
              variant: deleteInProgress
                ? BUTTON_VARIANTS.TEXT
                : BUTTON_VARIANTS.PRIMARY,
            },
          ]}
          dataTestId="remove_api_contract_sbdialog"
          id="remove_api_contract_sbdialog"
          onClose={() => setDeleteDialogOpen(false)}
          title={"Remove API contract"}
          isOpen={deleteDialogOpen}
          className="bundle_share_dialog_box"
        >
          This action cancels the contract for API (
          <b>{elementToBeDeleted?.contractName}</b>). Are you sure?
          <br />
          <br />
        </Dialog>
      }
    </TableContainer>
  );
}
